import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveOnboardingStatus, resolveOnboardingStatusSync } from '../lib/onboardingStatus';

export function OnboardingGate({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isOnboardingRoute = location.pathname === '/onboarding';

  // Fast-path on first render: existing users with a local team or the
  // skip-deferred flag never see a blank frame. Only fresh installs need
  // to wait on the remote check.
  const [checking, setChecking] = useState(() => {
    if (!user || isOnboardingRoute) return false;
    return resolveOnboardingStatusSync() === undefined;
  });

  useEffect(() => {
    if (!user || isOnboardingRoute) {
      setChecking(false);
      return;
    }

    const sync = resolveOnboardingStatusSync();
    if (sync) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    resolveOnboardingStatus()
      .then((status) => {
        if (cancelled) return;
        setChecking(false);
        if (status.needsOnboarding) {
          navigate('/onboarding', {
            replace: true,
            state: { from: `${location.pathname}${location.search}` },
          });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setChecking(false);
        navigate('/onboarding', {
          replace: true,
          state: { from: `${location.pathname}${location.search}` },
        });
      });

    return () => {
      cancelled = true;
    };
  }, [isOnboardingRoute, location.pathname, location.search, navigate, user?.id]);

  if (checking && !isOnboardingRoute) return null;
  return <>{children}</>;
}
